/**
 * ImageBrowser Component - Browse and Select Previously Uploaded Images
 * @date_of_change: 2025-10-09
 *
 * This component allows users to browse their previously uploaded images from S3
 * and select one to use for their character or monster artwork.
 *
 * Features:
 * - Grid display of uploaded images with thumbnails
 * - Filter by type (token/map/all)
 * - Click to select and apply to current entity
 * - Shows upload date and file info
 */

// @date_of_change: 2025-10-09 - Fixed import: use resolveUploadedKey to resolve S3 URLs
import React, { useState, useEffect, useCallback } from 'react';
import { resolveUploadedKey } from '../../services/ArtworkResolver';
import { apiFetch } from '../../utils/apiFetch';
import {
  PREMIUM_PLAN_NAME,
  UPGRADE_ROUTE,
  getTierConfig,
  isPremiumTier
} from '../../config/entitlements';
import { getAssetUsageSnapshot } from '../../services/EntitlementService';
import { resolveRuntimeTier } from '../../utils/runtimeTier';
import './ImageBrowser.css';

// Thumbnail component that resolves S3 URLs
const ImageThumbnail = ({ imageKey, alt }) => {
  const [resolvedUrl, setResolvedUrl] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      try {
        const url = await resolveUploadedKey(imageKey);
        if (!cancelled) {
          setResolvedUrl(url);
        }
      } catch (err) {
        console.error('[ImageThumbnail] Failed to resolve URL:', err);
        if (!cancelled) {
          setResolvedUrl('/placeholders/token-default.svg');
        }
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [imageKey]);

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      onError={(e) => {
        e.target.src = '/placeholders/token-default.svg';
      }}
    />
  );
};

const ImageBrowser = ({ onSelectImage, onClose, filterType = 'token' }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const tier = resolveRuntimeTier();
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);
  const assetCap = tierConfig.limits.assetCount;

  const loadImages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const customAssetManager = (await import('../../services/CustomAssetManager')).default;
      const uploadedImages = await customAssetManager.listUploadedImages(filterType);
      const usage = await getAssetUsageSnapshot();

      console.log('[ImageBrowser] Loaded images:', uploadedImages);
      setImages(uploadedImages);
      setAssetUsage(usage);
    } catch (err) {
      console.error('[ImageBrowser] Failed to load images:', err);
      setError('Failed to load images: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadImages();
  }, [filterType, loadImages]);

  const handleSelectImage = (image) => {
    // Return the S3 key (e.g., "uploads/user_XXX/token/filename.png")
    console.log('[ImageBrowser] Selected image:', image);
    onSelectImage(image.key);
  };

  const handleDeleteImage = async (image, event) => {
    event.stopPropagation();

    if (!window.confirm('Delete this image from your vault?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/s3-uploads/${image.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('Delete failed');
      }
      await loadImages();
    } catch (err) {
      setError(`Failed to delete image: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="image-browser">
        <div className="image-browser-header">
          <h3>Browse Uploaded Images</h3>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
        <div className="image-browser-loading">
          <div className="spinner"></div>
          <p>Loading images...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="image-browser">
        <div className="image-browser-header">
          <h3>Browse Uploaded Images</h3>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
        <div className="image-browser-error">
          <p>{error}</p>
          <button onClick={loadImages} className="btn btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="image-browser">
      <div className="image-browser-header">
        <div>
          <h3>Browse Uploaded Images ({images.length})</h3>
          <p className="image-browser-plan-copy">
            {isPremium
              ? `${PREMIUM_PLAN_NAME}: keep stacking maps and tokens in your vault.`
              : `Free vault: ${assetUsage.totalCount}/${assetCap} uploads used. Delete one to free a slot or unlock ${PREMIUM_PLAN_NAME}.`}
          </p>
        </div>
        <button onClick={onClose} className="btn btn-secondary">Close</button>
      </div>

      {images.length === 0 ? (
        <div className="image-browser-empty">
          <p>No images uploaded yet.</p>
          <p>Upload an image above to get started!</p>
        </div>
      ) : (
        <div className="image-browser-grid">
          {images.map((image) => (
            <div
              key={image.id}
              className="image-browser-item"
              onClick={() => handleSelectImage(image)}
            >
              <button
                type="button"
                className="image-browser-delete"
                onClick={(event) => handleDeleteImage(image, event)}
                title="Delete image"
              >
                ×
              </button>
              <div className="image-browser-thumbnail">
                <ImageThumbnail
                  imageKey={image.key}
                  alt={image.key}
                />
              </div>
              <div className="image-browser-info">
                <div className="image-browser-filename">
                  {image.key.split('/').pop()}
                </div>
                <div className="image-browser-meta">
                  {new Date(image.uploadedAt).toLocaleDateString()}
                  {' • '}
                  {(image.size / 1024).toFixed(0)} KB
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!isPremium && assetUsage.totalCount >= assetCap && (
        <div className="image-browser-upgrade">
          <span>Free vault full.</span>
          <button type="button" onClick={() => window.location.assign(UPGRADE_ROUTE)}>Upgrade</button>
        </div>
      )}
    </div>
  );
};

export default ImageBrowser;
